import React, { useEffect, useState } from 'react';
import { ScoresSearchParams } from '../../../models/overmind/search-params';
import { useAppActions, useAppState } from '../../../overmind';
import { SkeletonLoader } from '../../../components/general/loading-state/loaders/skeleton-loader/skeleton-loader';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { EntityScores } from '../../../models/overmind/analytics';
import { CompanyVariables } from '../../../models/view/company-variables';
import { DataPoint } from '../../../components/charts/recharts.models';
import { RadarChartComponent } from '../../../components/charts/radar-chart/radar-chart';
import { removeDuplicateScores } from '../../../components/charts/recharts.helpers';
import { WidgetCard } from '../analytics-filters/widget-card';

function transformScores(scores: EntityScores[], companyVariables: CompanyVariables): DataPoint[] {
  const uniqueScores = removeDuplicateScores(scores, 'name');

  return uniqueScores.map(score => ({
    name: score.name,
    ['Actual proficiency']: companyVariables.useLevels ? score.score?.level : score.score?.percentage,
    ['Expected proficiency']: score.comparisonScore ? (companyVariables.useLevels ? score.comparisonScore.level : score.comparisonScore.percentage) : undefined,
  }));
}

export function RoleProficiencyRadarWidget({ baseSearchParams }: { baseSearchParams: ScoresSearchParams }): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataPoint[]>();

  useEffect(() => {
    async function getData() {
      if (!baseSearchParams.roleSlug) return;
      setIsLoading(true);

      const response = await actions.analyticsGetScoresByScoreType(baseSearchParams);
      setData(transformScores(response?.results ?? [], companyVariables));

      setIsLoading(false);
    }

    getData();
  }, [baseSearchParams.roleSlug, baseSearchParams.learnerSlug, companyVariables.slug]);

  return isLoading ? (
    <SkeletonLoader height="400" />
  ) : (
    <WidgetCard title='Role proficiency radar' showSkillLevelHelp>
      {!!data && data?.length > 2 ? <RadarChartComponent data={data} /> : <EmptyState title="Not enough data" />}
    </WidgetCard>
  );
}
